import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { CalendarIcon, CheckIcon, Clock3Icon, MapPinIcon, MoonIcon, SearchIcon, SunIcon } from 'lucide-react';
import { services, type ServiceId } from '../data/services';
import type { CityTourDuration, RentalShift, TripSearch } from '../types/search';
interface BookingSearchProps {
  onSearch: (search: TripSearch) => void;
}
export function BookingSearch({
  onSearch
}: BookingSearchProps) {
  const [service, setService] = useState<ServiceId>('destination');
  const [shift, setShift] = useState<RentalShift>('day');
  const [pickup, setPickup] = useState('Kathmandu');
  const [drop, setDrop] = useState('Pokhara');
  const [city, setCity] = useState('Kathmandu');
  const [cityTourDuration, setCityTourDuration] = useState<CityTourDuration>('full-day');
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const activeService = services.find(item => item.id === service)!;
  const isCityTour = service === 'citytour';
  function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    onSearch({
      service,
      shift: isCityTour ? 'day' : shift,
      pickup: isCityTour ? city : pickup,
      drop: isCityTour ? city : drop,
      date,
      time,
      cityTourDuration: isCityTour ? cityTourDuration : undefined
    });
  }
  return <div id="book" className="rounded-2xl border border-slate-200 bg-white p-4 shadow-xl shadow-slate-900/5 sm:p-5" data-id="element-16" data-mp="2sns2ksfbt4t">
      <div className="flex flex-wrap gap-2" data-id="element-17" data-mp="hkplblzznnvv">
        {services.map(item => {
        const Icon = item.icon;
        const active = item.id === service;
        return <button key={item.id} type="button" onClick={() => setService(item.id)} className={`flex items-center gap-2 rounded-lg px-3.5 py-2 text-sm font-semibold transition-colors ${active ? 'bg-brand-500 text-white' : 'bg-slate-100 text-ink-soft hover:bg-slate-200'}`} data-id="element-18" data-mp="qirskjabfcrl">
              <Icon className="h-4 w-4" data-id="element-19" data-mp="r983m4qwjzv6" />
              {item.short}
            </button>;
      })}
      </div>

      <p className="mt-3 text-sm text-ink-soft" data-id="element-20" data-mp="w8uhmgek1k51">{activeService.description}</p>

      {isCityTour ? <form onSubmit={handleSubmit} className="mt-4 grid gap-3 md:grid-cols-[1.25fr_1fr_1fr_1.3fr]" data-id="element-21" data-mp="115t7gmr6r4xf">
          <Field label="City name" data-id="element-22" data-mp="15994vlb2x9b6">
            <MapPinIcon className="h-4 w-4 text-slate-400" data-id="element-23" data-mp="1564s3zjjt58w" />
            <input required value={city} onChange={event => setCity(event.target.value)} placeholder="Kathmandu or Pokhara" className="w-full bg-transparent text-sm outline-none placeholder:text-slate-400" data-id="element-24" data-mp="1a6rezf0u1ukr" />
          </Field>
          <Field label="Tour date" data-id="element-25" data-mp="1jzgpa6knd2ei">
            <CalendarIcon className="h-4 w-4 text-slate-400" data-id="element-26" data-mp="1ow712hpf5bu4" />
            <input required type="date" value={date} onChange={event => setDate(event.target.value)} className="w-full bg-transparent text-sm text-ink outline-none" data-id="element-27" data-mp="1oyjimtr9lk6n" />
          </Field>
          <Field label="Start time" data-id="element-28" data-mp="1xxojw7u7nrmi">
            <Clock3Icon className="h-4 w-4 text-slate-400" data-id="element-29" data-mp="3if5y950b528p" />
            <input required type="time" value={time} onChange={event => setTime(event.target.value)} className="w-full bg-transparent text-sm text-ink outline-none" data-id="element-30" data-mp="3igh4rcl50o3i" />
          </Field>
          <div data-id="element-31" data-mp="39falwwv0n173">
            <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500" data-id="element-32" data-mp="39ercy5ncfn57">Tour duration</span>
            <div className="flex h-[42px] rounded-lg border border-slate-200 p-1" data-id="element-33" data-mp="34hr4jo30u8ax">
              <DurationButton active={cityTourDuration === 'half-day'} onClick={() => setCityTourDuration('half-day')} label="Half day" data-id="element-34" data-mp="52dqqghlzzxo" />
              <DurationButton active={cityTourDuration === 'full-day'} onClick={() => setCityTourDuration('full-day')} label="Full day" data-id="element-35" data-mp="3sctw43t7mpv3" />
            </div>
          </div>
          <div className="md:col-span-4" data-id="element-36" data-mp="3ncfy7elbowas">
            <SearchButton data-id="element-37" data-mp="ooife9p618xf" />
          </div>
        </form> : <form onSubmit={handleSubmit} className="mt-4 grid gap-3 md:grid-cols-5" data-id="element-38" data-mp="jxk09dfwygvr">
          <Field label={service === 'airport' ? 'Pickup point' : 'From'} data-id="element-39" data-mp="jvpc7q9ci8ng">
            <MapPinIcon className="h-4 w-4 text-slate-400" data-id="element-40" data-mp="3ghmo97fha7p" />
            <input required value={pickup} onChange={event => setPickup(event.target.value)} placeholder={service === 'airport' ? 'Airport terminal' : 'City or address'} className="w-full bg-transparent text-sm outline-none placeholder:text-slate-400" data-id="element-41" data-mp="8hngybgql5jc" />
          </Field>
          <Field label="To" data-id="element-42" data-mp="n60cj7bwwmdb">
            <MapPinIcon className="h-4 w-4 text-slate-400" data-id="element-43" data-mp="n5s0tirizn3p" />
            <input required value={drop} onChange={event => setDrop(event.target.value)} placeholder="Destination" className="w-full bg-transparent text-sm outline-none placeholder:text-slate-400" data-id="element-44" data-mp="n8e8dief22lx" />
          </Field>
          <Field label="Pickup date" data-id="element-45" data-mp="if1wm8uu5qal">
            <CalendarIcon className="h-4 w-4 text-slate-400" data-id="element-46" data-mp="ie0hntap8jnl" />
            <input required type="date" value={date} onChange={event => setDate(event.target.value)} className="w-full bg-transparent text-sm text-ink outline-none" data-id="element-47" data-mp="1655udzlcevto" />
          </Field>
          <Field label="Pickup time" data-id="element-48" data-mp="1kaeltsv62twx">
            <Clock3Icon className="h-4 w-4 text-slate-400" data-id="element-49" data-mp="1k9d0mvkfsr1h" />
            <input required type="time" value={time} onChange={event => setTime(event.target.value)} className="w-full bg-transparent text-sm text-ink outline-none" data-id="element-50" data-mp="1g8t51wfihvhg" />
          </Field>
          <div data-id="element-51" data-mp="1u226nax7xcpf">
            <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500" data-id="element-52" data-mp="1p1ej4z877dzy">Shift</span>
            <div className="flex h-[42px] rounded-lg border border-slate-200 p-1" data-id="element-53" data-mp="11b0r38pm5f5d">
              <ShiftButton active={shift === 'day'} onClick={() => setShift('day')} icon={<SunIcon className="h-4 w-4" data-id="element-55" data-mp="xc1jthget6ar" />} label="Day" data-id="element-54" data-mp="xa8cs11vwk28" />
              <ShiftButton active={shift === 'night'} onClick={() => setShift('night')} icon={<MoonIcon className="h-4 w-4" data-id="element-57" data-mp="16adlqfmq7qii" />} label="Night" data-id="element-56" data-mp="1baj6dbukwcr3" />
            </div>
          </div>
          <div className="md:col-span-5" data-id="element-58" data-mp="2modg7b2synyd">
            <SearchButton data-id="element-59" data-mp="2mn248nz7e9w6" />
          </div>
        </form>}

      <p className="mt-3 flex items-center gap-1.5 text-xs text-ink-soft" data-id="element-60" data-mp="2hqlj82nawbnb">
        {isCityTour ? <MapPinIcon className="h-3.5 w-3.5 text-brand-500" data-id="element-61" data-mp="2pr1aogv6wi9t" /> : shift === 'day' ? <SunIcon className="h-3.5 w-3.5 text-accent" data-id="element-62" data-mp="2ts394vha8qaq" /> : <MoonIcon className="h-3.5 w-3.5 text-brand-500" data-id="element-63" data-mp="2tqs4yujxdgj3" />}
        {isCityTour ? `${cityTourDuration === 'half-day' ? 'Half-day' : 'Full-day'} city tour with a verified local driver.` : shift === 'day' ? 'Day charges apply (06:00 – 20:00).' : 'Night charges apply (20:00 – 06:00) — a surcharge is added.'}
      </p>
    </div>;
}
function SearchButton() {
  return <motion.button whileTap={{
    scale: 0.98
  }} type="submit" className="flex w-full items-center justify-center gap-2 rounded-lg bg-accent px-5 py-3 text-sm font-bold text-ink transition-colors hover:brightness-95" data-id="element-64" data-mp="2b1c1pzguv9yz"><SearchIcon className="h-4 w-4" data-id="element-65" data-mp="2krx13p4csiiy" />Search available cars</motion.button>;
}
function Field({
  label,
  children
}: {
  label: string;
  children: React.ReactNode;
}) {
  return <div data-id="element-66" data-mp="1na6mnmiqolpq"><span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500" data-id="element-67" data-mp="1sb45807nvhrr">{label}</span><div className="flex h-[42px] items-center gap-2 rounded-lg border border-slate-200 px-3 focus-within:border-brand-400" data-id="element-68" data-mp="1krl2c9ewits9">{children}</div></div>;
}
function ShiftButton({
  active,
  onClick,
  icon,
  label
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
}) {
  return <button type="button" onClick={onClick} className={`flex flex-1 items-center justify-center gap-1.5 rounded-md text-sm font-semibold transition-colors ${active ? 'bg-brand-500 text-white' : 'text-ink-soft'}`} data-id="element-69" data-mp="1av8cjmmh902s">{icon}{label}</button>;
}
function DurationButton({
  active,
  onClick,
  label
}: {
  active: boolean;
  onClick: () => void;
  label: string;
}) {
  return <button type="button" onClick={onClick} aria-pressed={active} className={`flex flex-1 items-center justify-center gap-1.5 rounded-md text-sm font-semibold transition-colors ${active ? 'bg-brand-500 text-white' : 'text-ink-soft'}`} data-id="element-70" data-mp="s3pabi0a1q1t">{active && <CheckIcon className="h-4 w-4" data-id="element-71" data-mp="i6km7cmvfkid" />}{label}</button>;
}